import { useCallback, useEffect, useState } from "react";
import { casesApi, cdssApi } from "@/lib/api";
import { triggerDownload, fileSafe } from "@/lib/download";
import { toast } from "sonner";

export function useCaseHistory(userName) {
  const [cases, setCases] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [exportingAll, setExportingAll] = useState(false);

  const load = useCallback(() => casesApi.list().then(setCases).catch(() => setCases([])), []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (id) => {
    if (openId === id) { setOpenId(null); setDetail(null); return; }
    setOpenId(id);
    setDetail(null);
    setLoadingDetail(true);
    try {
      setDetail(await casesApi.get(id));
    } catch {
      toast.error("Could not load case.");
    } finally {
      setLoadingDetail(false);
    }
  };

  const remove = async (id) => {
    try {
      await casesApi.remove(id);
      setCases((c) => c.filter((x) => x.id !== id));
      if (openId === id) { setOpenId(null); setDetail(null); }
      toast.success("Case deleted");
    } catch {
      toast.error("Could not delete case.");
    }
  };

  const safeDownload = async (apiFn, filename, errMsg) => {
    try {
      triggerDownload(await apiFn(), filename);
    } catch {
      toast.error(errMsg);
    }
  };

  const downloadPdf = (c) => safeDownload(
    () => cdssApi.finalReportPdf({ patient: c.patient, findings: c.findings, final: c.final }),
    `CDSS_Report_${fileSafe(c.patient?.name)}.pdf`, "Could not generate the PDF.");

  const exportAll = async () => {
    setExportingAll(true);
    try {
      const blob = await casesApi.exportPdf();
      triggerDownload(blob, `CDSS_Cases_${fileSafe(userName, "clinician")}.pdf`);
    } catch {
      toast.error("Could not export the case history.");
    } finally {
      setExportingAll(false);
    }
  };

  return { cases, openId, detail, loadingDetail, exportingAll, toggle, remove, downloadPdf, exportAll };
}

